import {
  Component,
  OnInit,
  inject,
  DestroyRef,
  ViewChild,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import {
  forkJoin,
  map,
  of,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { SkeletonModule } from 'primeng/skeleton';
import { DrawerModule } from 'primeng/drawer';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService, MessageService, MenuItem } from 'primeng/api';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EquipamentoService,
  PropriedadeEquipService,
  CategoriaEquipService,
  PostoService,
} from '@core/services/domain-services';
import { FeedbackService } from '@core/services/feedback.service';
import { EmptyStateComponent } from '@shared/components/empty-state/empty-state.component';
import { PaginatorControlComponent } from '@shared/components/paginator-control/paginator-control.component';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { EquipamentosNavComponent } from '../equipamentos-nav/equipamentos-nav.component';
import {
  Equipamento,
  EnumEstadoEquipamento,
  EnumMarcaCategoria,
} from '@core/models/models';
import { formatNomeCampo } from '@shared/utils/format-nome-campo.util';
import {
  EquipamentoKpiGridComponent,
  EquipamentoKpiItem,
} from '../components/equipamento-kpi-grid/equipamento-kpi-grid.component';
import { EquipamentoMaintenanceTabComponent } from '../components/equipamento-maintenance-tab/equipamento-maintenance-tab.component';
import { FrotaManutencaoTabComponent } from '../components/frota-manutencao-tab/frota-manutencao-tab.component';
import { NovoEquipamentoComponent } from '../novo-equipamento/novo-equipamento.component';
import { EquipmentPhotoComponent } from '../components/equipment-photo/equipment-photo.component';
import { DataSourceRegistryService } from '@core/services/data-source-registry.service';
import { EquipamentoFilterStateService, EquipamentoFilters, filtrarEquipamentos, filtrosApiEquipamentos } from '../services/equipamento-filter-state.service';
import { TiposEquipamentoComponent } from '../tipos-equipamento/tipos-equipamento.component';

const TIPO_VIATURAS = 2; // confirmado nos exemplos reais da API

export type ViaturaEstado = 'Activa' | 'Inactiva' | 'Em Manutenção';

@Component({
  selector: 'app-viaturas-page',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    MenuModule,
    SkeletonModule,
    TooltipModule,
    DrawerModule,
    EmptyStateComponent,
    PaginatorControlComponent,
    EquipamentosNavComponent,
    EquipamentoKpiGridComponent,
    EquipmentPhotoComponent,
    FrotaManutencaoTabComponent,
    NovoEquipamentoComponent,
    TiposEquipamentoComponent,
  ],
  templateUrl: './viaturas.component.html',
  styleUrl: './viaturas.component.scss',
})
export class ViaturasPageComponent implements OnInit {
  @ViewChild('rowMenu') rowMenu!: any;

  private readonly svc = inject(EquipamentoService);
  private readonly propriedadeSvc = inject(PropriedadeEquipService);
  private readonly msg = inject(MessageService);
  private readonly feedback = inject(FeedbackService);
  private readonly confirm = inject(ConfirmationService);
  readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly categoriaSvc = inject(CategoriaEquipService);
  private readonly postoSvc = inject(PostoService);
  private readonly dataSourceRegistry = inject(DataSourceRegistryService);
  private readonly filterSvc = inject(EquipamentoFilterStateService);

  equipamentos: Equipamento[] = [];
  pageFirst = 0;
  pageSize = 10;
  today = new Date();
  loading = false;
  loadingPropriedades = false;
  menuItems: MenuItem[] = [];
  totalRecords = 0;
  navTotal: number | null = null;
  IdCategoriaEquipamento: number | null = null;
  private activeFilters: EquipamentoFilters = {};
  private readonly marcaNomes = new Map<string, string>();
  private readonly modeloNomes = new Map<string, string>();
  private readonly postoNomes = new Map<string, string>();

  showCreateDrawer = false;
  readonly maintenanceTabVisible = false;
  searchTerm = false;
  activeSection: 'list' | 'maintenance' | 'configuration' = 'list';

  onMaintenanceCountChange(count: number): void {
    this.maintenanceApiCount = count;
    this.cdr.markForCheck();
  }

  readonly formatNomeCampo = formatNomeCampo;

  readonly estadoOptions = [
    { label: 'Todos os estados', value: null },
    { label: 'Activa', value: EnumEstadoEquipamento.BOM },
    { label: 'Inactiva', value: EnumEstadoEquipamento.INATIVO },
    { label: 'Em Manutenção', value: 'MANUTENCAO' },
  ];

  ngOnInit(): void {
    this.postoSvc
      .getAll({ pageSize: 200 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: any) => {
        const items = res?.data?.items ?? res?.data ?? res ?? [];
        if (Array.isArray(items)) {
          for (const p of items) {
            const id = p?.id ?? p?.Id ?? p?.postoId ?? p?.PostoId;
            const nome = p?.nome ?? p?.Nome ?? p?.nomePosto ?? p?.NomePosto ?? p?.designacao ?? p?.Designacao;
            if (id != null && nome) this.postoNomes.set(String(id), String(nome));
          }
          this.cdr.markForCheck();
        }
      });

    this.filterSvc.filters$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.activeFilters = filters;
        this.pageFirst = 0;
        this.load();
      });
  }

  load(): void {
    this.loading = true;
    const pageNumber = Math.floor(this.pageFirst / this.pageSize) + 1;

    this.svc
      .getAll({
        ...filtrosApiEquipamentos(this.activeFilters),
        IdTipoEquipamento: TIPO_VIATURAS,
        pageNumber,
        pageSize: Object.keys(this.activeFilters).length ? 1000 : this.pageSize,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.equipamentos = response.data ?? [];
          this.carregarNomesMarcaModelo(this.equipamentos);
          this.carregarNomesPostos(this.equipamentos);
          this.totalRecords = response.totalRecords ?? this.equipamentos.length;
          this.navTotal = this.totalRecords;

          if (
            this.equipamentos.length === 0 &&
            this.totalRecords > 0 &&
            this.pageFirst >= this.totalRecords
          ) {
            this.pageFirst = Math.max(0, this.pageFirst - this.pageSize);
            this.load();
            return;
          }

          this.loading = false;
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.equipamentos = [];
          this.totalRecords = 0;
          this.navTotal = 0;
          this.loading = false;
          this.feedback.showError(err, 'Não foi possível carregar a listagem de viaturas.');
          this.cdr.detectChanges();
        },
      });
  }

  get filteredEquipamentos(): Equipamento[] {
    return filtrarEquipamentos(this.equipamentos, this.activeFilters);
  }

  get paginatedEquipamentos(): Equipamento[] {
    return this.equipamentos;
  }

  onPageChange(event: { first?: number; rows?: number }): void {
    const nextRows = event.rows ?? this.pageSize;
    this.pageFirst = nextRows !== this.pageSize ? 0 : (event.first ?? 0);
    this.pageSize = nextRows;
    this.load();
  }

  get kpiItems(): EquipamentoKpiItem[] {
    return [
      {
        icon: 'pi pi-car',
        iconClass: 'total',
        value: this.kpiTotal,
        label: 'Total Viaturas',
      },
      {
        icon: 'ri-checkbox-circle-line',
        iconClass: 'bom',
        value: this.kpiAtivas,
        label: 'Activas',
        valueClass: 'bom',
      },
      {
        icon: 'ri-pause-circle-line',
        iconClass: 'inativo',
        value: this.kpiInativas,
        label: 'Inactivas',
        valueClass: 'inativo',
      },
      {
        icon: 'ri-shield-check-line',
        iconClass: 'seguro',
        value: this.kpiSeguros,
        label: 'Seguros Válidos',
      },
      {
        icon: 'ri-money-dollar-circle-line',
        iconClass: 'valor',
        value: this.formatKz(this.kpiValor),
        label: 'Valor Total',
      },
    ];
  }

  get kpiTotal(): number {
    return this.totalRecords;
  }
  get kpiAtivas(): number {
    return this.paginatedEquipamentos.filter(
      (e) => this.getViaturaEstado(e) === 'Activa'
    ).length;
  }
  get kpiInativas(): number {
    return this.paginatedEquipamentos.filter(
      (e) => this.getViaturaEstado(e) === 'Inactiva'
    ).length;
  }
  get maintenanceCount(): number {
    return this.maintenanceApiCount;
  }
  maintenanceApiCount = 0;
  get kpiValor(): number {
    return this.paginatedEquipamentos.reduce((a, e) => a + (e.valorAquisicao ?? 0), 0);
  }
  get kpiSeguros(): number {
    const now = new Date();
    return this.paginatedEquipamentos.filter((e) => {
      const val = this.getPropriedadeValor(e, 'VALIDADE_SEGURO') || this.getPropriedadeValor(e, 'SEGURO');
      return val && new Date(val) > now;
    }).length;
  }

  getPropriedadeValor(e: Equipamento, nome: string): string {
    const nomeNormalizado = nome.toUpperCase();

    if (nomeNormalizado === 'MARCA' && e.marca) {
      return this.marcaNomes.get(String(e.marca)) ?? String(e.marca);
    }

    if (nomeNormalizado === 'MODELO' && e.modelo) {
      return this.modeloNomes.get(String(e.modelo)) ?? String(e.modelo);
    }

    const propriedade = e.propriedades?.find(
      (p) => p.nome?.toUpperCase() === nomeNormalizado,
    );
    const valor = propriedade?.valor ?? '';
    const valorDescricao = (propriedade as any)?.valorDescricao;

    if (valorDescricao) {
      return String(valorDescricao);
    }

    if (nomeNormalizado === 'MARCA') {
      return this.marcaNomes.get(String(valor)) ?? valor;
    }

    if (nomeNormalizado === 'MODELO') {
      return this.modeloNomes.get(String(valor)) ?? valor;
    }

    return valor;
  }

  getMatricula(e: Equipamento): string {
    return (
      this.getPropriedadeValor(e, 'MATRICULA') ||
      this.getPropriedadeValor(e, 'PLACA') ||
      e.codigoInterno ||
      '—'
    );
  }

  getMarcaModelo(e: Equipamento): string {
    const marca = this.getPropriedadeValor(e, 'MARCA');
    const modelo = this.getPropriedadeValor(e, 'MODELO');
    const parts = [marca, modelo].filter(Boolean);
    return parts.length > 0 ? parts.join(' · ') : '—';
  }

  getAno(e: Equipamento): string {
    return (
      this.getPropriedadeValor(e, 'ANO_FABRICO') ||
      this.getPropriedadeValor(e, 'ANO') ||
      ''
    );
  }

  getMotorista(e: Equipamento): string {
    return (
      this.getPropriedadeValor(e, 'MOTORISTA_RESPONSAVEL') ||
      this.getPropriedadeValor(e, 'MOTORISTA_NOME') ||
      this.getPropriedadeValor(e, 'NOME_MOTORISTA') ||
      this.getPropriedadeValor(e, 'MOTORISTA') ||
      this.getPropriedadeValor(e, 'CONDUTOR') ||
      this.getPropriedadeValor(e, 'RESPONSAVEL') ||
      this.getPropriedadeValor(e, 'MOTORISTA_ATRIBUIDO') ||
      (e as any).motoristaResponsavel?.nome ||
      (e as any).nomeMotorista ||
      (e as any).motorista?.nome ||
      (e as any).motorista ||
      '—'
    );
  }

  getKmAtual(e: Equipamento): string {
    const val =
      this.getPropriedadeValor(e, 'KM_ATUAL') ||
      this.getPropriedadeValor(e, 'KM_ACTUAL') ||
      this.getPropriedadeValor(e, 'KM_ULTIMA_REVISAO') ||
      this.getPropriedadeValor(e, 'KM_INICIAL') ||
      this.getPropriedadeValor(e, 'QUILOMETRAGEM') ||
      this.getPropriedadeValor(e, 'KILOMETRAGEM') ||
      this.getPropriedadeValor(e, 'KM') ||
      this.getPropriedadeValor(e, 'HORAS_USO') ||
      (e as any).kmAtual ||
      (e as any).kmActual ||
      (e as any).kmUltimaRevisao ||
      (e as any).quilometragem;

    if (!val) return '—';
    const num = Number(String(val).replace(/[^0-9.]/g, ''));
    if (!isNaN(num) && num > 0) {
      return `${new Intl.NumberFormat('pt-AO').format(num)} km`;
    }
    return String(val).includes('km') ? String(val) : `${val} km`;
  }

  getPostoAtribuido(e: Equipamento): string {
    const equipamento = e as any;
    const propriedades = equipamento.propriedades ?? equipamento.Propriedades ?? [];
    const propriedadePosto = propriedades.find((p: any) =>
      ['POSTO_ATRIBUIDO', 'POSTO'].includes(String(p?.nome ?? p?.Nome ?? '').toUpperCase()),
    );
    const valorDescricaoPosto = String(propriedadePosto?.valorDescricao ?? propriedadePosto?.ValorDescricao ?? '').trim();
    const valorPosto = String(propriedadePosto?.valor ?? propriedadePosto?.Valor ?? '').trim();
    const posto = equipamento.posto ?? equipamento.Posto;
    const postoNomePropriedade = valorDescricaoPosto && Number.isNaN(Number(valorDescricaoPosto))
      ? valorDescricaoPosto
      : valorPosto && Number.isNaN(Number(valorPosto))
        ? valorPosto
        : null;
    const postoNome =
      (posto?.nome && String(posto.nome).trim()) ||
      (posto?.Nome && String(posto.Nome).trim()) ||
      posto?.nomePosto ||
      posto?.NomePosto ||
      posto?.designacao ||
      posto?.Designacao ||
      equipamento.postoNome ||
      equipamento.nomePosto ||
      equipamento.PostoNome ||
      postoNomePropriedade;

    const rawPostoId =
      equipamento.idPosto ??
      equipamento.IdPosto ??
      equipamento.postoId ??
      equipamento.PostoId ??
      (posto?.id > 0 ? posto.id : null) ??
      (posto?.Id > 0 ? posto.Id : null) ??
      (Number.isFinite(Number(valorPosto)) && Number(valorPosto) > 0 ? valorPosto : null);

    const idStr = rawPostoId != null && rawPostoId !== '' ? String(rawPostoId) : null;
    const nomeFromMap = idStr ? this.postoNomes.get(idStr) : null;

    return postoNome || nomeFromMap || '—';
  }

  private carregarNomesPostos(equipamentos: Equipamento[]): void {
    const ids = new Set(
      equipamentos
        .map((equipamento) => {
          const item = equipamento as any;
          const propriedades = item.propriedades ?? item.Propriedades ?? [];
          const postoProp = propriedades.find((p: any) =>
            ['POSTO_ATRIBUIDO', 'POSTO'].includes(String(p?.nome ?? p?.Nome ?? '').toUpperCase()),
          );
          return (
            item.idPosto ??
            item.IdPosto ??
            item.postoId ??
            item.PostoId ??
            ((item.posto ?? item.Posto)?.id > 0 ? (item.posto ?? item.Posto).id : null) ??
            ((item.posto ?? item.Posto)?.Id > 0 ? (item.posto ?? item.Posto).Id : null) ??
            postoProp?.valor ??
            postoProp?.Valor
          );
        })
        .filter((id) => id != null && id !== '')
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );
    const idsPorCarregar = [...ids].filter((id) => !this.postoNomes.has(String(id)));
    if (!idsPorCarregar.length) return;

    forkJoin(idsPorCarregar.map((id) =>
      this.postoSvc.getById(id).pipe(
        catchError(() => of(null)),
        // Keep the requested ID available even if the API omits it in the body.
        map((posto) => ({ requestedId: id, posto })),
      ),
    ))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultados: any[]) => {
        for (const { requestedId, posto } of resultados) {
          const data = (posto as any)?.data ?? posto;
          const id = data?.id ?? data?.Id ?? requestedId;
          const nome = data?.nome ?? data?.Nome ?? data?.nomePosto ??
            data?.NomePosto ?? data?.designacao ?? data?.Designacao;
          if (id != null && nome) this.postoNomes.set(String(id), String(nome));
        }
        this.cdr.markForCheck();
      });
  }

  getSeguroAte(e: Equipamento): string {
    const val =
      this.getPropriedadeValor(e, 'VALIDADE_SEGURO') ||
      this.getPropriedadeValor(e, 'SEGURO');
    return val ? this.formatDate(val) : '—';
  }

  getProximaRevisao(e: Equipamento): string {
    const dataRev =
      this.getPropriedadeValor(e, 'PROXIMA_REVISAO_DATA') ||
      this.getPropriedadeValor(e, 'PROXIMA_REVISAO');
    const kmRev = this.getPropriedadeValor(e, 'PROXIMA_REVISAO_KM');

    if (dataRev && kmRev) {
      return `${this.formatDate(dataRev)} (${kmRev} km)`;
    }
    if (dataRev) return this.formatDate(dataRev);
    if (kmRev) return `${kmRev} km`;
    return '—';
  }

  getViaturaEstado(e: Equipamento): ViaturaEstado {
    const estadoStr = String(e.estado ?? '').toUpperCase();
    const estadoProp = this.getPropriedadeValor(e, 'ESTADO_VIATURA')?.toUpperCase();
    const statusProp = this.getPropriedadeValor(e, 'ESTADO')?.toUpperCase();

    if (
      estadoStr === 'MANUTENCAO' ||
      estadoStr === 'EM_MANUTENCAO' ||
      estadoStr === 'EM MANUTENÇÃO' ||
      estadoStr === 'EM MANUTENCAO' ||
      estadoProp === 'MANUTENCAO' ||
      estadoProp === 'EM_MANUTENCAO' ||
      estadoProp === 'EM MANUTENÇÃO' ||
      statusProp === 'MANUTENCAO' ||
      statusProp === 'EM_MANUTENCAO' ||
      statusProp === 'EM MANUTENÇÃO' ||
      (e as any).emManutencao === true
    ) {
      return 'Em Manutenção';
    }

    if (
      e.ativo === false ||
      e.estado === EnumEstadoEquipamento.INATIVO ||
      estadoStr === 'INATIVO' ||
      estadoStr === 'INACTIVO' ||
      estadoStr === '3' ||
      estadoStr === 'DESTRUIDO' ||
      estadoStr === '2' ||
      estadoStr === 'SUSPENSO' ||
      estadoProp === 'INATIVO' ||
      estadoProp === 'INACTIVO' ||
      estadoProp === 'SUSPENSO'
    ) {
      return 'Inactiva';
    }

    return 'Activa';
  }

  getViaturaEstadoClass(e: Equipamento): string {
    const estado = this.getViaturaEstado(e);
    if (estado === 'Activa') return 'badge-bom';
    if (estado === 'Em Manutenção') return 'badge-warning';
    return 'badge-inativo';
  }

  private carregarNomesMarcaModelo(equipamentos: Equipamento[]): void {
    const marcaIds = [
      ...new Set(
        equipamentos
          .map((e) => this.getRawPropertyValue(e, 'MARCA'))
          .filter((value) => /^\d+$/.test(String(value))),
      ),
    ];

    if (!marcaIds.length) {
      return;
    }

    this.dataSourceRegistry
      .resolve('MARCAS_VEICULO', EnumMarcaCategoria.VEICULO, true)
      .pipe(catchError(() => of([])), takeUntilDestroyed(this.destroyRef))
      .subscribe((marcas) => {
        marcas.forEach((marca) => {
          this.marcaNomes.set(String(marca.value), marca.label);
        });

        const modeloRequests = marcaIds.map((marcaId) =>
          this.dataSourceRegistry
            .resolve('MODELOS_POR_MARCA', marcaId, true)
            .pipe(catchError(() => of([]))),
        );

        forkJoin(modeloRequests)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((modelosPorMarca) => {
            modelosPorMarca.flat().forEach((modelo) => {
              this.modeloNomes.set(String(modelo.value), modelo.label);
            });
            this.cdr.detectChanges();
          });

        this.cdr.detectChanges();
      });
  }

  private getRawPropertyValue(e: Equipamento, nome: string): string {
    return (
      e.propriedades?.find(
        (p) => p.nome?.toUpperCase() === nome.toUpperCase(),
      )?.valor ?? ''
    );
  }

  isSeguroExpirado(e: Equipamento): boolean {
    const val = this.getPropriedadeValor(e, 'VALIDADE_SEGURO') || this.getPropriedadeValor(e, 'SEGURO');
    return !!val && new Date(val) < new Date();
  }

  verDetalhe(e: Equipamento): void {
    if (e.id) this.router.navigate(['/admin/equipamentos', e.id]);
  }

  openCreateDrawer(): void {
    if (this.IdCategoriaEquipamento !== null) {
      this.showCreateDrawer = true;
      return;
    }

    this.categoriaSvc
      .getAll({ pageSize: 100 })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.IdCategoriaEquipamento =
            response.data?.find((category) =>
              (category.designacao ?? '').toUpperCase().includes('VIATUR'),
            )?.id ?? null;
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
        error: (error) => {
          console.warn('Categorias indisponíveis:', error?.status);
          this.showCreateDrawer = true;
          this.cdr.detectChanges();
        },
      });
  }

  onEquipmentSaved(): void {
    this.showCreateDrawer = false;
    this.pageFirst = 0;
    this.feedback.showSuccess('Viatura registada com sucesso.');
    this.load();
  }

  openRowMenu(event: Event, e: Equipamento): void {
    const estado = this.getViaturaEstado(e);
    const items: MenuItem[] = [];

    // Ver Detalhe
    items.push({
      icon: 'pi pi-eye',
      label: 'Ver Detalhe',
      command: () => this.verDetalhe(e),
    });

    // Edita
    items.push({
      icon: 'pi pi-pencil',
      label: 'Editar',
      command: () =>
        this.router.navigate(['/admin/equipamentos', e.id, 'editar']),
    });

    // Contextual options per state
    if (estado === 'Activa') {
      items.push({
        icon: 'pi pi-wrench',
        label: 'Registar Manutenção',
        command: () => this.registarManutencao(e),
      });
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-pause-circle',
        label: 'Suspender',
        styleClass: 'menu-danger',
        command: () => this.suspenderViatura(e),
      });
    } else if (estado === 'Inactiva') {
      items.push({
        visible: false,
        icon: 'pi pi-wrench',
        label: 'Registar Manutenção',
        command: () => this.registarManutencao(e),
      });
      items.push({
        icon: 'pi pi-check-circle',
        label: 'Ativar',
        styleClass: 'menu-success',
        command: () => this.ativarViatura(e),
      });
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-trash',
        label: 'Remover',
        styleClass: 'menu-danger',
        command: () => this.confirmDelete(e),
      });
    } else if (estado === 'Em Manutenção') {
      items.push({
        icon: 'pi pi-check',
        label: 'Concluir Manutenção',
        styleClass: 'menu-success',
        command: () => this.concluirManutencao(e),
      });
      items.push({ separator: true });
      items.push({
        icon: 'pi pi-pause-circle',
        label: 'Suspender',
        styleClass: 'menu-danger',
        command: () => this.suspenderViatura(e),
      });
    }

    this.menuItems = items;
    this.rowMenu.toggle(event);
  }

  registarManutencao(e: Equipamento): void {
    if (this.getViaturaEstado(e) !== 'Activa') {
      this.feedback.showWarning('Uma viatura inativa n\u00e3o pode receber uma nova manuten\u00e7\u00e3o.');
      return;
    }

    if (!e.id) return;
    this.router.navigate(['/admin/equipamentos', e.id], {
      queryParams: { tab: 'manutencao', acao: 'registar' },
    });
  }

  concluirManutencao(e: Equipamento): void {
    if (!e.id) return;
    this.router.navigate(['/admin/equipamentos', e.id], {
      queryParams: { tab: 'manutencao', acao: 'concluir' },
    });
  }

  suspenderViatura(e: Equipamento): void {
    if (!e.id) return;
    const matricula = this.getMatricula(e);
    this.confirm.confirm({
      header: 'Suspender Viatura',
      message: `Tem a certeza que deseja suspender a viatura "${matricula}"? O seu estado passará para Inactiva.`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Suspender',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.loading = true;
        this.svc
          .update(e.id!, {
            ...e,
            ativo: false,
            estado: EnumEstadoEquipamento.INATIVO,
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.feedback.showSuccess(`Viatura ${matricula} suspensa com sucesso.`);
              this.load();
            },
            error: (err) => {
              this.loading = false;
              this.feedback.showError(err, 'Não foi possível suspender a viatura.');
              this.cdr.detectChanges();
            },
          });
      },
    });
  }

  ativarViatura(e: Equipamento): void {
    if (!e.id) return;
    const matricula = this.getMatricula(e);
    this.confirm.confirm({
      header: 'Ativar Viatura',
      message: `Deseja reativar a viatura "${matricula}"?`,
      icon: 'pi pi-check-circle',
      acceptLabel: 'Ativar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-success',
      accept: () => {
        this.loading = true;
        this.svc
          .update(e.id!, {
            ...e,
            ativo: true,
            estado: EnumEstadoEquipamento.BOM,
          })
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.feedback.showSuccess(`Viatura ${matricula} ativada com sucesso.`);
              this.load();
            },
            error: (err) => {
              this.loading = false;
              this.feedback.showError(err, 'Não foi possível ativar a viatura.');
              this.cdr.detectChanges();
            },
          });
      },
    });
  }

  confirmDelete(e: Equipamento): void {
    const matricula = this.getMatricula(e);
    this.confirm.confirm({
      header: 'Remover Viatura',
      message: `A viatura "${matricula}" será permanentemente removida. Deseja continuar?`,
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        if (!e.id) return;
        this.svc
          .delete(e.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.feedback.showSuccess(`Viatura ${matricula} removida com sucesso.`);
              this.load();
            },
            error: (err) => {
              this.feedback.showError(err, 'Erro ao remover viatura.');
            },
          });
      },
    });
  }

  formatKz(v?: number): string {
    if (v == null) return '—';
    return (
      new Intl.NumberFormat('pt-AO', { maximumFractionDigits: 0 }).format(v) +
      ' Kz'
    );
  }

  formatDate(d?: string): string {
    if (!d) return '—';
    try {
      return new Date(d).toLocaleDateString('pt-AO');
    } catch {
      return d;
    }
  }
}
